import { useState } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Alert, Button, Card, DataTable, SectionCard, type ColumnDef } from "@/deps/ui-library";
import {
  useOpenBills,
  usePartyDetails,
  usePartyStanding,
  useSupplierReconciliations,
  useWithholdingCodes,
} from "@/api/queries";
import type {
  BillSummary,
  PartyForm,
  PartyStanding,
  SupplierReconciliation,
  WithholdingCode,
} from "@/api/types";
import { useCan, useTaxWords } from "@/app/session";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { KpiStrip, type Kpi } from "@/components/KpiStrip";
import { PageHeader } from "@/components/PageHeader";
import { PermittedAction } from "@/components/PermittedAction";
import { SegmentControl } from "@/components/SegmentControl";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { termsLabel } from "@/lib/paymentTerms";
import { withholdingBase } from "@/lib/settlement";
import { PartyDialog } from "./PartiesPage";
import { AgeingStrip, DetailRow, LedgerTab } from "./PartyLedger";
import { ReconcileStatementDialog } from "./ReconcileStatementDialog";
import { ageText, paisa } from "./standing";

type Tab = "ledger" | "open" | "details";

/**
 * One supplier: where they stand, every line between you, and what you still owe them.
 *
 * The figures are a report, so someone without `reports.view` still gets the supplier's details and
 * open bills. Marking a statement reconciled compares it with those figures, so it needs both.
 */
export function SupplierDetailPage() {
  const say = useTaxWords();
  const { id } = useParams({ from: "/purchases/suppliers/$id" });
  const search = useSearch({ from: "/purchases/suppliers/$id" });
  const navigate = useNavigate();
  const can = useCan();
  const seesFigures = can("reports.view");
  const asAt = today();

  const { data: party, error } = usePartyDetails(id);
  const { data: standing } = usePartyStanding(id, "supplier", asAt, seesFigures);
  const { data: open } = useOpenBills(id);
  const { data: reconciliations } = useSupplierReconciliations(id, seesFigures);
  const { data: codes } = useWithholdingCodes();
  const category = codes?.find((code) => code.code === party?.withholdingCode);
  const [editing, setEditing] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const tab: Tab = search.tab ?? (seesFigures ? "ledger" : "open");
  const name = party ? party.tradingName || party.legalName : "";

  if (error) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        description={(error as Error).message}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumbs items={[{ label: "Suppliers", to: "/purchases/suppliers" }, { label: name }]} />
      <PageHeader
        title={name}
        subtitle={party ? metaLine(party, say) : undefined}
        actions={
          <span className="flex flex-wrap gap-2">
            {seesFigures && can("purchases.payments.create") ? (
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                onClick={() => setReconciling(true)}
              >
                Reconcile statement
              </Button>
            ) : null}
            <PermittedAction
              permission="purchases.bills.create"
              variant="secondary"
              onClick={() =>
                void navigate({ to: "/purchases/bills/new", search: { supplier: id } })
              }
            >
              New bill
            </PermittedAction>
            <PermittedAction
              permission="purchases.payments.create"
              onClick={() =>
                void navigate({
                  to: "/purchases/payments/new",
                  search: { bill: undefined, supplier: id },
                })
              }
            >
              Record payment
            </PermittedAction>
          </span>
        }
      />

      {seesFigures ? (
        <>
          <KpiStrip
            loading={!standing}
            items={standing ? tiles(standing, open ?? [], category) : []}
          />
          {standing ? <AgeingStrip standing={standing} /> : null}
          {reconciliations ? (
            <p className="text-fg-muted text-xs">
              {reconciliations[0]
                ? `Last reconciled ${formatDate(reconciliations[0].statementDate)} · ${reconciledText(reconciliations[0])}`
                : "No statement has been reconciled yet."}
            </p>
          ) : null}
        </>
      ) : null}

      <SegmentControl<Tab>
        label="Supplier sections"
        value={tab}
        onChange={(next) =>
          void navigate({
            to: "/purchases/suppliers/$id",
            params: { id },
            search: { ...search, tab: next === "ledger" ? undefined : next },
            replace: true,
          })
        }
        segments={[
          ...(seesFigures ? [{ value: "ledger" as const, label: "Ledger" }] : []),
          { value: "open", label: "Open bills", badge: open?.length },
          { value: "details", label: "Details" },
        ]}
      />

      {tab === "ledger" && seesFigures ? (
        <LedgerTab
          id={id}
          kind="supplier"
          name={name}
          from={search.from ?? standing?.fiscalYearStart ?? asAt}
          to={search.to ?? asAt}
          setDate={(key, value) =>
            void navigate({
              to: "/purchases/suppliers/$id",
              params: { id },
              search: { ...search, [key]: value },
              replace: true,
            })
          }
        />
      ) : null}
      {tab === "open" ? <OpenBillsTab asAt={asAt} bills={open} category={category} /> : null}
      {tab === "details" && party ? (
        <DetailsTab
          party={party}
          category={category}
          reconciliations={seesFigures ? reconciliations : undefined}
          onEdit={can("settings.parties.manage") ? () => setEditing(true) : undefined}
        />
      ) : null}

      {editing && party ? (
        <PartyDialog kind="supplier" id={id} initial={party} onClose={() => setEditing(false)} />
      ) : null}
      {reconciling ? (
        <ReconcileStatementDialog id={id} name={name} onClose={() => setReconciling(false)} />
      ) : null}
    </div>
  );
}

function metaLine(party: PartyForm, say: (text: string) => string): string {
  return [
    party.pan ? say(`PAN ${party.pan}`) : "",
    party.phone,
    party.email,
    [party.street, party.city].filter(Boolean).join(", "),
    party.paymentTermsDays === "" ? "" : termsLabel(party.paymentTermsDays),
  ]
    .filter(Boolean)
    .join(" · ");
}

function reconciledText(last: SupplierReconciliation): string {
  const difference = paisa(last.difference);
  return difference === 0
    ? "Balances agreed"
    : `Difference of ${formatAmount(Math.abs(difference) / 100)} noted`;
}

/** What a category would withhold from a bill if what's open on it were paid today, in paisa. */
function withheldOn(bill: BillSummary, category: WithholdingCode | undefined): number {
  const rate = Number(category?.ratePct ?? "0");
  return Math.round((withholdingBase(bill, paisa(bill.balance)) * rate) / 100);
}

function tiles(
  standing: PartyStanding,
  open: BillSummary[],
  category: WithholdingCode | undefined,
): Kpi[] {
  const openCount = open.length;
  const overdue = paisa(standing.overdue) > 0;

  return [
    {
      label: "Owed",
      value: formatAmount(standing.outstanding),
      detail: `${openCount} open ${openCount === 1 ? "bill" : "bills"}`,
    },
    {
      label: "Overdue",
      value: formatAmount(standing.overdue),
      detail: overdue
        ? `Oldest ${standing.oldestOverdueDays} ${standing.oldestOverdueDays === 1 ? "day" : "days"} past due`
        : "Nothing past due",
      tone: overdue ? "bad" : "default",
    },
    {
      label: "Paid this year",
      value: formatAmount(standing.settledThisYear),
      detail: "Fiscal year to date, tax withheld included",
    },
    category
      ? {
          label: "Withholding on open bills",
          value: formatAmount(
            open.reduce((sum, bill) => sum + withheldOn(bill, category), 0) / 100,
          ),
          detail: category.ratePct ? `${category.name} · ${category.ratePct}%` : category.name,
        }
      : {
          label: "Withholding on open bills",
          value: "None",
          detail: "No withholding on this supplier",
        },
  ];
}

function OpenBillsTab({
  asAt,
  bills,
  category,
}: {
  asAt: string;
  bills: BillSummary[] | undefined;
  category: WithholdingCode | undefined;
}) {
  const say = useTaxWords();
  const columns: Array<ColumnDef<BillSummary>> = [
    {
      id: "document",
      header: "Document",
      cell: ({ row: { original: bill } }) => (
        <Link
          to="/purchases/bills/$id"
          params={{ id: bill.id }}
          className="text-primary hover:underline"
        >
          {bill.number}
        </Link>
      ),
    },
    {
      id: "date",
      header: "Bill date",
      cell: ({ row: { original: bill } }) => formatDate(bill.date),
    },
    {
      id: "due",
      header: "Due",
      cell: ({ row: { original: bill } }) => (bill.due ? formatDate(bill.due) : ""),
    },
    {
      id: "amount",
      header: "Amount",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: bill } }) => formatAmount(bill.total),
    },
    {
      id: "paid",
      header: "Paid",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: bill } }) =>
        formatAmount((paisa(bill.total) - paisa(bill.balance)) / 100),
    },
    {
      id: "open",
      header: "Open",
      meta: { textAlign: "right", cellClassName: "num font-medium" },
      cell: ({ row: { original: bill } }) => formatAmount(bill.balance),
    },
    // Always there, because a table keeps the order it first drew and the category arrives later.
    {
      id: "withholding",
      header: say("TDS on payment"),
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: bill } }) =>
        category ? formatAmount(withheldOn(bill, category) / 100) : "",
    },
    {
      id: "ageing",
      header: "Ageing",
      cell: ({ row: { original: bill } }) => ageText(asAt, bill.date, bill.due),
    },
  ];
  const total = (bills ?? []).reduce((sum, bill) => sum + paisa(bill.balance), 0);

  return (
    <div className="flex flex-col gap-2">
      <Card size="none" className="overflow-hidden">
        <DataTable
          aria-label="Open bills"
          size="sm"
          enableSorting={false}
          columns={columns}
          data={bills ?? []}
          getRowId={(bill) => bill.id}
          loading={bills === undefined}
          emptyContent="Nothing is open for this supplier."
          pagination={{ mode: "manual", show: false }}
        />
      </Card>
      <p className="text-fg-muted text-xs">
        {bills?.length ?? 0} open · {formatAmount(total / 100)} outstanding
      </p>
    </div>
  );
}

function DetailsTab({
  party,
  category,
  reconciliations,
  onEdit,
}: {
  party: PartyForm;
  category: WithholdingCode | undefined;
  reconciliations: SupplierReconciliation[] | undefined;
  onEdit: (() => void) | undefined;
}) {
  const say = useTaxWords();
  const history: Array<ColumnDef<SupplierReconciliation>> = [
    {
      id: "date",
      header: "Statement date",
      cell: ({ row: { original: entry } }) => formatDate(entry.statementDate),
    },
    {
      id: "statement",
      header: "On statement",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: entry } }) => formatAmount(entry.statementBalance),
    },
    {
      id: "book",
      header: "In Rigo",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: entry } }) => formatAmount(entry.bookBalance),
    },
    {
      id: "difference",
      header: "Difference",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: entry } }) => formatAmount(entry.difference),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {onEdit ? (
        <div>
          <Button colorScheme="default" variant="outline" size="sm" onClick={onEdit}>
            Edit supplier
          </Button>
        </div>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
        <SectionCard title="Identity">
          <dl>
            <DetailRow label="Legal name" value={party.legalName} />
            <DetailRow label="Trading name" value={party.tradingName} />
            <DetailRow label={say("PAN")} value={party.pan} />
            <DetailRow
              label={say("Registered for VAT")}
              value={party.vatRegistered ? "Yes" : "No"}
            />
          </dl>
        </SectionCard>
        <SectionCard title="Contact">
          <dl>
            <DetailRow label="Phone" value={party.phone} />
            <DetailRow label="Email" value={party.email} />
          </dl>
        </SectionCard>
        <SectionCard title="Address">
          <dl>
            <DetailRow label="Street" value={party.street} />
            <DetailRow label="City" value={party.city} />
          </dl>
        </SectionCard>
        <SectionCard title="Payment">
          <dl>
            <DetailRow label="Payment terms" value={termsLabel(party.paymentTermsDays)} />
            <DetailRow label="Withholding category" value={category?.name ?? "None"} />
          </dl>
        </SectionCard>
      </div>
      {reconciliations ? (
        <SectionCard title="Statement reconciliations" flush>
          <DataTable
            aria-label="Statement reconciliations"
            size="sm"
            enableSorting={false}
            columns={history}
            data={reconciliations}
            getRowId={(entry) => entry.id}
            emptyContent="No statement has been compared with the books yet."
            pagination={{ mode: "manual", show: false }}
          />
        </SectionCard>
      ) : null}
    </div>
  );
}
